import React, { useState, useRef, useEffect } from "react";
import { MessageSquareText, Mail, Clock, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Typage des messages de contact (à aligner avec votre API)
interface ContactMessage {
  id: number;
  name: string;
  email: string;
  message: string;
  createdAt: string; // ou Date
  read: boolean;
}

// Données fictives (à remplacer par appel API)
const initialMessages: ContactMessage[] = [
  {
    id: 1,
    name: "Sophie Martin",
    email: "sophie.martin@example.com",
    message: "Bonjour, je suis intéressée par vos services de branding. Pouvez-vous me contacter ?",
    createdAt: "2025-04-18T10:30:00",
    read: false,
  },
  {
    id: 2,
    name: "Thomas Bernard",
    email: "thomas.bernard@example.com",
    message: "Merci pour votre devis. Je souhaiterais discuter des délais.",
    createdAt: "2025-04-17T15:20:00",
    read: false,
  },
  {
    id: 3,
    name: "Julie Dubois",
    email: "julie.dubois@example.com",
    message: "Félicitations pour votre travail, j'aimerais collaborer.",
    createdAt: "2025-04-16T09:45:00",
    read: true,
  },
];

const MessageButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ContactMessage[]>(initialMessages);
  const menuRef = useRef<HTMLDivElement>(null);
  const unreadCount = messages.filter((m) => !m.read).length;

  // Fermeture au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Marquer un message comme lu
  const markAsRead = (id: number) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, read: true } : msg))
    );
  };

  // Formater la date relative
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return "Hier";
    return `Il y a ${diffDays} jours`;
  };

  // Tronquer le message
  const truncateMessage = (msg: string, maxLen = 50) => {
    if (msg.length <= maxLen) return msg;
    return msg.substring(0, maxLen) + "...";
  };

  return (
    <div className="sm:relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 text-white shadow-[0_2px_8px_rgba(22,241,158,0.2)] hover:from-primary/30 hover:to-secondary/30 transition-all duration-200 border border-white/10 hover:scale-105"
      >
        <MessageSquareText size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary rounded-full text-dark ring-2 ring-dark">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-5 sm:right-0 mt-2 w-80 rounded-2xl bg-dark/95 backdrop-blur-md border border-white/10 shadow-2xl overflow-hidden z-50"
          >
            {/* En-tête */}
            <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-primary/5 to-secondary/5">
              <h3 className="text-sm font-semibold text-white">Messages de contact</h3>
            </div>

            {/* Liste des messages */}
            <div className="max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  Aucun message
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`relative px-4 py-3 border-b border-white/5 transition-colors hover:bg-white/5 cursor-pointer ${
                      !msg.read ? "bg-primary/5" : ""
                    }`}
                    onClick={() => markAsRead(msg.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <User size={14} className="text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-white truncate">
                            {msg.name}
                          </p>
                          <p className="text-[10px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                            <Clock size={10} />
                            {formatRelativeTime(msg.createdAt)}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {truncateMessage(msg.message)}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Mail size={10} className="text-gray-500" />
                          <span className="text-[10px] text-gray-500 truncate">
                            {msg.email}
                          </span>
                        </div>
                      </div>
                      {!msg.read && (
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pied de page : lien vers la page des messages */}
            <div className="px-4 py-2 border-t border-white/10 text-center">
              <a
                href="/dashboard/messages"  // adaptez à votre route
                className="text-xs text-primary hover:text-secondary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Voir tous les messages
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageButton;